// ── Key constants ─────────────────────────────────────────────────────────────
import type { ModulePermission } from '../types/permissions.types';

const ACCESS_TOKEN_KEY  = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_ID_KEY       = 'user_id';
const EMAIL_KEY         = 'email';
const NAME_KEY          = 'name';
const ROLE_KEY          = 'role';
const PERMISSIONS_KEY   = 'permissions';
const USER_IMAGE_KEY    = 'user_image';

// ── Access token ──────────────────────────────────────────────────────────────
export const getAccessToken = (): string | null =>
  localStorage.getItem(ACCESS_TOKEN_KEY);

export const setAccessToken = (token: string): void =>
  localStorage.setItem(ACCESS_TOKEN_KEY, token);

// ── Refresh token ─────────────────────────────────────────────────────────────
export const getRefreshToken = (): string | null =>
  localStorage.getItem(REFRESH_TOKEN_KEY);

export const setRefreshToken = (token: string): void =>
  localStorage.setItem(REFRESH_TOKEN_KEY, token);

// ── User profile fields ───────────────────────────────────────────────────────
export const getUserId    = (): string | null => localStorage.getItem(USER_ID_KEY);
export const getEmail     = (): string | null => localStorage.getItem(EMAIL_KEY);
export const getName      = (): string | null => localStorage.getItem(NAME_KEY);
export const getRole      = (): string | null => localStorage.getItem(ROLE_KEY);
export const getUserImage = (): string | null => localStorage.getItem(USER_IMAGE_KEY);

/** Persist the permissions array to localStorage as JSON. */
export function setPermissions(permissions: ModulePermission[]): void {
  localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(permissions));
}

/** Read back the permissions array. Returns [] if missing or malformed. */
export function getPermissions(): ModulePermission[] {
  try {
    const raw = localStorage.getItem(PERMISSIONS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export const setUserFields = (fields: {
  user_id:      string;
  email:        string;
  name:         string;
  role:         string;
  user_image?:  string | null;
}): void => {
  localStorage.setItem(USER_ID_KEY,    fields.user_id);
  localStorage.setItem(EMAIL_KEY,      fields.email);
  localStorage.setItem(NAME_KEY,       fields.name);
  localStorage.setItem(ROLE_KEY,       fields.role);
  localStorage.setItem(USER_IMAGE_KEY, fields.user_image ?? '');
};

// ── JWT payload decode (no library) ──────────────────────────────────────────
export const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const base64 = token.split('.')[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    return JSON.parse(atob(base64)) as Record<string, unknown>;
  } catch {
    return null;
  }
};

// Returns true when the stored access token is missing or expired.
// Uses a 10-second buffer so we refresh slightly before actual expiry.
export const isAccessTokenExpired = (): boolean => {
  const token = getAccessToken();
  if (!token) return true;
  const payload = decodeJwtPayload(token);
  if (!payload?.['exp']) return true;
  return ((payload['exp'] as number) - 10) < (Date.now() / 1000);
};

// Atomically removes every auth key — call on logout or refresh failure.
export const clearTokens = (): void => {
  [
    ACCESS_TOKEN_KEY,
    REFRESH_TOKEN_KEY,
    USER_ID_KEY,
    EMAIL_KEY,
    NAME_KEY,
    ROLE_KEY,
    PERMISSIONS_KEY,
    USER_IMAGE_KEY,
  ].forEach(key => localStorage.removeItem(key));
};
